/**
 * De-identified illustrative mockups for Grace AI.
 * No real patient names, free-text answers, dates of birth, or identifiable quotes.
 */
import { MessageCircleIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';

const panels = [
  {
    id: 'check-in',
    title: 'Daily check-in',
    caption: 'Short prompts · sleep · energy · mood · comfort',
    icon: MessageCircleIcon,
    body: (
      <div className="space-y-3">
        <div className="bg-background/70 border-border/60 rounded-xl border p-3">
          <p className="text-muted-foreground text-xs tracking-wide uppercase">Today’s prompt</p>
          <p className="text-foreground mt-1 text-sm">How is your energy this morning?</p>
        </div>
        <div className="flex gap-2">
          {['Low', 'Okay', 'Steady', 'Strong'].map((label, index) => (
            <span
              key={label}
              className={
                index === 1
                  ? 'bg-cta/20 text-cta border-cta/30 rounded-full border px-3 py-1 text-xs font-medium'
                  : 'bg-background/50 text-muted-foreground border-border/50 rounded-full border px-3 py-1 text-xs'
              }
            >
              {label}
            </span>
          ))}
        </div>
        <div className="bg-background/50 border-border/50 h-16 rounded-xl border border-dashed p-3">
          <p className="text-muted-foreground text-xs">Optional note — illustrated only; no patient free text shown.</p>
        </div>
      </div>
    )
  },
  {
    id: 'insights',
    title: 'Structured insights',
    caption: 'Patterns over days — not diagnoses',
    icon: SparklesIcon,
    body: (
      <div className="space-y-3">
        {[
          { label: 'Sleep consistency', value: 'Stable · 4 days' },
          { label: 'Energy trend', value: 'Soft dip · mid-week' },
          { label: 'Comfort report', value: 'Monitored' }
        ].map((row) => (
          <div
            key={row.label}
            className="bg-background/60 border-border/50 flex items-center justify-between rounded-xl border px-3 py-2.5"
          >
            <span className="text-foreground text-sm">{row.label}</span>
            <span className="text-muted-foreground text-xs tabular-nums">{row.value}</span>
          </div>
        ))}
      </div>
    )
  },
  {
    id: 'escalation',
    title: 'Care-team escalation',
    caption: 'Human review path · never silent on safety signals',
    icon: ShieldCheckIcon,
    body: (
      <div className="space-y-3">
        <div className="border-cta/30 bg-cta/10 rounded-xl border p-3">
          <p className="text-cta text-xs font-medium tracking-wide uppercase">Escalation pathway</p>
          <p className="text-foreground mt-1 text-sm leading-relaxed">
            Safety-related responses route to a real person on the care team — immediately, without exception.
          </p>
        </div>
        <ul className="text-muted-foreground space-y-2 text-sm">
          <li className="flex gap-2">
            <span className="bg-cta mt-1.5 size-1.5 shrink-0 rounded-full" aria-hidden />
            No automated clinical advice
          </li>
          <li className="flex gap-2">
            <span className="bg-primary mt-1.5 size-1.5 shrink-0 rounded-full" aria-hidden />
            Structured summary for clinicians
          </li>
          <li className="flex gap-2">
            <span className="bg-urgency mt-1.5 size-1.5 shrink-0 rounded-full" aria-hidden />
            Patient privacy preserved in public demos
          </li>
        </ul>
      </div>
    )
  }
] as const;

export function GraceMockups() {
  return (
    <section aria-labelledby="grace-mockups-heading" className="px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <p className="text-cta mb-3 text-xs font-medium tracking-[0.2em] uppercase">Product panels</p>
        <h2
          id="grace-mockups-heading"
          className="font-display text-foreground text-2xl font-semibold tracking-tight sm:text-3xl"
        >
          Structured check-ins, de-identified
        </h2>
        <p className="text-muted-foreground mt-3 max-w-3xl text-base leading-relaxed">
          Illustrative interface frames only — layout-matched to Grace AI&apos;s check-in and escalation model.
          No patient-identifying data is displayed.
        </p>

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {panels.map((panel) => {
            const Icon = panel.icon;
            return (
              <figure
                key={panel.id}
                className="border-border/70 bg-card/60 overflow-hidden rounded-2xl border shadow-sm backdrop-blur-sm"
              >
                <div className="border-border/60 bg-background/40 flex items-center gap-3 border-b px-4 py-3">
                  <span className="bg-cta/15 text-cta flex size-9 items-center justify-center rounded-lg">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <div>
                    <figcaption className="text-foreground text-sm font-medium">{panel.title}</figcaption>
                    <p className="text-muted-foreground text-xs">{panel.caption}</p>
                  </div>
                </div>
                <div className="p-4">{panel.body}</div>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
